import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Spin, Button, Input, message, Card, Typography, Grid, Form } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { courses } from '../data/courses'
import PageLayout from './PageLayout'
import homeBack from '../assets/home_back.jpg'

const { Title, Text, Paragraph } = Typography
const { useBreakpoint } = Grid

const CourseProtectedRouteComponent: React.FC<{ children: React.ReactNode; courseId: string; required?: boolean }> = ({
  children,
  courseId,
  required = true
}) => {
  const screens = useBreakpoint()
  const isMobile = !screens.lg

  // Show loading spinner with proper background
  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#66b9bf',
        backgroundImage: `url(${homeBack})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: isMobile ? '140% auto' : '100% auto',
        backgroundPosition: isMobile ? '50% 0' : 'center 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative'
      }}
    >
      <Spin size="large" tip={undefined} />
    </div>
  )
}

interface CourseProtectedRouteProps {
  children: React.ReactNode
  courseId: string
  required?: boolean // If true, redirect to login if not authenticated
}

const CourseProtectedRoute: React.FC<CourseProtectedRouteProps> = ({
  children,
  courseId,
  required = true
}) => {
  const { user, userProfile, loading, hasCourseAccess, addOrderId, isAnonymous } = useAuth()
  const location = useLocation()
  const [orderIdInput, setOrderIdInput] = useState('')
  const [addingOrderId, setAddingOrderId] = useState(false)
  const { t, i18n } = useTranslation('onlineCourse')
  const screens = useBreakpoint()

  const isMobile = !screens.lg

  // Get course title
  const getCourseTitle = () => {
    const course = courses[courseId]
    if (!course) return courseId

    const currentLang = i18n.resolvedLanguage || 'en-GB'
    const title = course.titleTranslations?.[currentLang]
    if (title) {
      return title
    }
    // Fallback to British English if translation not found
    return course.titleTranslations?.['en-GB'] || course.id.charAt(0).toUpperCase() + course.id.slice(1) + ' Course'
  }

  const courseTitle = getCourseTitle()

  // Show loading spinner while checking auth
  if (loading) {
    return <CourseProtectedRouteComponent courseId={courseId} required={required}><></></CourseProtectedRouteComponent>
  }

  // If authentication is required and user is not logged in
  if (required && !user) {
    return <Navigate to="/auth" state={{ from: location }} replace />
  }

  // If user is logged in but doesn't have course access, show order ID entry
  if (user && !hasCourseAccess(courseId)) {
    const handleAddOrderId = async (values: { orderId: string }) => {
      if (!values.orderId.trim()) {
        message.error('Please enter an Order ID')
        return
      }

      setAddingOrderId(true)
      const success = await addOrderId(courseId, values.orderId.trim())
      setAddingOrderId(false)

      if (success) {
        message.success('Course access granted!')
        setOrderIdInput('')
      } else {
        message.error('Invalid Order ID format. Please check and try again.')
      }
    }

    return (
      <PageLayout style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <Card
          style={{
            background: 'rgba(241, 248, 248, 0.637)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            border: 'none',
            maxWidth: '600px',
            width: '100%'
          }}
          bodyStyle={{ padding: '40px 32px' }}
        >
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <Title level={3} style={{ margin: 0, color: '#333', fontWeight: '800' }}>
              {String(t('courseAccess.title', { courseTitle: courseTitle }))}
            </Title>
            <Text type="secondary" style={{ display: 'block', marginTop: '8px' }}>
              {isAnonymous
                ? String(t('courseAccess.subtitleGuest'))
                : String(t('courseAccess.subtitle'))
              }
            </Text>
          </div>

          <Form onFinish={handleAddOrderId} layout="vertical" size="large">
            <Form.Item
              label={String(t('courseAccess.orderIdLabel'))}
              name="orderId"
              rules={[
                { required: true, message: String(t('courseAccess.orderIdRequired')) },
                {
                  pattern: /^\d{3}-\d{7}-\d{7}$/,
                  message: String(t('courseAccess.orderIdInvalid'))
                }
              ]}
              extra={String(t('courseAccess.orderIdFormat'))}
            >
              <Input
                placeholder="123-4567890-1234567"
                value={orderIdInput}
                onChange={(e) => setOrderIdInput(e.target.value)}
                size="large"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={addingOrderId}
                block
                size="large"
                style={{
                  height: '48px',
                  borderRadius: '8px',
                  fontWeight: '600'
                }}
              >
                {String(t('courseAccess.grantAccessButton'))}
              </Button>
            </Form.Item>
          </Form>

          <div style={{ marginTop: '32px', textAlign: 'center' }}>
            <Text type="secondary" style={{ fontSize: '14px' }}>
              <strong>{String(t('courseAccess.howToFindTitle'))}</strong>
            </Text>
            <div style={{ fontSize: '14px', color: '#666', marginTop: '8px' }} dangerouslySetInnerHTML={{ __html: String(t('courseAccess.howToFindInstructions')) }} />
          </div>
        </Card>
      </PageLayout>
    )
  }

  // User has access or authentication not required
  return <>{children}</>
}

export default CourseProtectedRoute